import {ChangeDetectorRef, Component} from '@angular/core';
import {
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    Validators
} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {LoaderService} from '@services/loader.service';
import {UserRoleService} from '@services/user-role.service';
import {ToastrService} from 'ngx-toastr';
import {Observable, forkJoin, tap} from 'rxjs';
import {
    UserRolePermissions,
    Section,
    Permission
} from '@/common/interface/user.role.permission';

@Component({
    selector: 'app-edit-user-permission',
    templateUrl: './edit-user-permission.component.html',
    styleUrls: ['./edit-user-permission.component.scss']
})
export class EditUserPermissionComponent {
    roleForm: FormGroup;
    sections: Section[] = [];
    permissions: Permission[] = [];
    public isAuthLoading = false;
    public isLoading$: Observable<boolean>;
    private roleId: number;

    constructor(
        private fb: FormBuilder,
        private userRoleService: UserRoleService,
        private toastr: ToastrService,
        private loaderService: LoaderService,
        private route: ActivatedRoute,
        private changeDetectorRef: ChangeDetectorRef,
        private router: Router
    ) {
        this.isLoading$ = this.loaderService.isLoading$;
    }

    ngOnInit(): void {
        this.loadSectionsAndPermissions().subscribe(() => {
            this.route.paramMap.subscribe((params) => {
                this.roleId = +params.get('id');
                if (this.roleId) {
                    this.loadUserRolePermissions(this.roleId);
                }
            });
        });
        this.roleForm = this.fb.group({
            roleName: ['', Validators.required],
            dashboard: this.fb.group({
                sectionId: [null],
                sectionName: ['Dashboard'],
                permissions: this.fb.group({})
            }),
            catalog: this.fb.array([])
        });
        // this.initializeDashboardPermissions(null);
    }

    loadSectionsAndPermissions(): Observable<any> {
        const sections$ = this.userRoleService.getSectionList();
        const permissions$ = this.userRoleService.getPermissioList();

        return forkJoin([sections$, permissions$]).pipe(
            tap(([sections, permissions]) => {
                this.sections = sections;
                this.permissions = permissions;
                this.initializeForm();
            })
        );
    }

    initializeForm() {
        this.setDashboardSectionId();
        this.initializeDashboardPermissions();
        this.initializeCatalogPermissions();
    }

    setDashboardSectionId() {
        const dashboardSection = this.sections.find(
            (section) => section.sectionName.toLowerCase() === 'dashboard'
        );
        if (dashboardSection) {
            this.roleForm
                .get('dashboard.sectionId')
                .setValue(dashboardSection.id);
        }
    }

    initializeDashboardPermissions(roleData?: UserRolePermissions) {
        const dashboardGroup = this.roleForm.get(
            'dashboard.permissions'
        ) as FormGroup;

        // Find the sectionId dynamically based on sectionName
        const dashboardSection = roleData?.permissions.find(
            (p) => p.sectionName === 'dashboard'
        );
        const sectionId = dashboardSection ? dashboardSection.sectionId : 0; // Default to 9 if not found
        this.permissions.forEach((permission) => {
            const existingPermission = roleData?.permissions.find(
                (p) =>
                    p.permissionId === permission.id &&
                    p.sectionId === sectionId
            );
            const isGranted = existingPermission
                ? existingPermission.isGranted
                : false;

            if (dashboardGroup.get(permission.permissionName)) {
                dashboardGroup.patchValue({
                    [permission.permissionName]: isGranted
                });
            } else {
                dashboardGroup.addControl(
                    permission.permissionName,
                    new FormControl(isGranted)
                );
            }
        });
        this.changeDetectorRef.detectChanges();
    }

    initializeCatalogPermissions(roleData?: UserRolePermissions | null) {
        const catalogFormArray = this.roleForm.get('catalog') as FormArray;
        catalogFormArray.clear(); // Clear existing controls

        this.sections
            .filter(
                (section) => section.sectionName.toLowerCase() !== 'dashboard'
            ) // Exclude the dashboard from the catalog
            .forEach((section) => {
                const sectionGroup = this.fb.group({
                    sectionName: [section.sectionName],
                    permissions: this.fb.group({})
                });

                this.permissions.forEach((permission) => {
                    // Find if this permissionId and sectionId exist in the roleData
                    const existingPermission = roleData?.permissions.find(
                        (p) =>
                            p.permissionId === permission.id &&
                            p.sectionId === section.id
                    );

                    // Set the control to true if isGranted is true, otherwise false
                    const isGranted = existingPermission
                        ? existingPermission.isGranted
                        : false;

                    // Add control with default value based on isGranted
                    (sectionGroup.get('permissions') as FormGroup).addControl(
                        permission.permissionName,
                        new FormControl(isGranted)
                    );
                });

                catalogFormArray.push(sectionGroup);
            });
    }
    loadUserRolePermissions(roleId: number) {
        this.userRoleService
            .getRolePermissioListByRoleId(roleId)
            .subscribe((roleData: UserRolePermissions) => {
                if (roleData) {
                    this.roleForm.patchValue({
                        roleName: roleData.roleName
                    });
                    this.initializeDashboardPermissions(roleData); // Ensure roleData is not undefined
                    this.initializeCatalogPermissions(roleData);
                } else {
                    this.toastr.error('Failed to load role permissions.');
                }
            });
    }

    onSubmit() {
        this.isAuthLoading = true;
        this.loaderService.showLoader();

        if (this.roleForm.invalid) {
            this.roleForm.markAllAsTouched();
            this.isAuthLoading = false;
            this.loaderService.hideLoader();
            return;
        }

        const userPermissions = [];
        const dashboardGroup = this.roleForm.get('dashboard') as FormGroup;
        const dashboardPermissions = dashboardGroup.get('permissions').value;
        const dashboardSectionId = dashboardGroup.get('sectionId').value;
        for (const permission of this.permissions) {
            userPermissions.push({
                permissionId: permission.id,
                sectionId: dashboardSectionId,
                isGranted: dashboardPermissions[permission.permissionName] // Check this value
            });
        }

        this.catalogFormArray.controls.forEach((sectionControl) => {
            const sectionPermissions = sectionControl.get('permissions').value;
            const sectionName = sectionControl.get('sectionName').value;
            const section = this.sections.find(
                (s) => s.sectionName === sectionName
            );
            const sectionId = section ? section.id : null;
            if (sectionId !== null) {
                for (const permission of this.permissions) {
                    userPermissions.push({
                        permissionId: permission.id,
                        sectionId: sectionId,
                        isGranted: sectionPermissions[permission.permissionName]
                    });
                }
            }
        });

        //console.log('User Permissions:', userPermissions); // Log the final userPermissions for review
        // return userPermissions;
        const roleData = {
            id: 0, // Assuming roleId is available in the component
            roleId: this.roleId, // You already have this
            roleName: this.roleForm.get('roleName').value, // Get the role name from the form
            permissions: userPermissions // Final list of permissions
        };
        this.userRoleService.updateUserRole(roleData).subscribe({
            next: (response: any) => {
                setTimeout(() => {
                    this.isAuthLoading = false;
                    this.loaderService.hideLoader();
                    this.toastr.success('Permissions updated successfully');
                    this.router.navigate(['/user-permission-list']);
                }, 2000);
                this.roleForm.reset();

                this.initializeForm();
            },
            error: (error) => {
                this.toastr.error('Failed to update permissions.');
                setTimeout(() => {
                    this.isAuthLoading = false;
                    this.loaderService.hideLoader();
                    // Handle login error
                    this.toastr.error(error.error);
                }, 2000);
            }
        });
    }

    get catalogFormArray(): FormArray {
        return this.roleForm.get('catalog') as FormArray;
    }
}
